import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/admin/AuthContext";
import { Lock, Eye, EyeOff, ShieldAlert, AlertTriangle } from "lucide-react";

/**
 * StaffLogin — Cinematic, intentionally mysterious login for /staff vault.
 * Same JWT cookie auth as /admin, but visually disguised: no PURELIFE branding,
 * no admin references, no "forgot password" CTA. If the URL leaks, the page
 * looks like an internal terminal — discouraging guessing.
 */
export default function StaffLogin() {
  const { user, login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.email) {
    const from = location.state?.from?.pathname || "/staff/bible";
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Accesso negato");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="staff-shell min-h-screen flex items-center justify-center px-4 py-12">
      <div className="staff-scanlines" aria-hidden="true" />
      <div className="staff-vignette" aria-hidden="true" />

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl border border-[#E26B6B]/35 bg-[#1a0a0e]/70 mb-6">
            <Lock size={24} className="text-[#E26B6B]" strokeWidth={1.5} />
          </div>
          <p className="font-mono-pl text-[10px] uppercase tracking-[0.42em] text-[#E26B6B]/85 mb-3">
            Terminale Sicuro
          </p>
          <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-wide text-[#F5F5F5]">
            Accesso Riservato
          </h1>
          <p className="mt-3 text-xs text-[#B6B6BE] max-w-xs mx-auto leading-relaxed">
            Questo settore non esiste pubblicamente. Se sei qui per errore,
            chiudi questa scheda e dimentica l&apos;URL.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="staff-login-form"
          className="staff-card rounded-2xl p-8 sm:p-9"
        >
          <label className="block mb-5">
            <span className="block font-mono-pl text-[10px] uppercase tracking-[0.28em] text-[#B6B6BE] mb-2">
              Identificativo
            </span>
            <input
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="staff-login-email"
              className="staff-input w-full px-4 py-3 rounded-lg text-sm text-[#F5F5F5] font-mono-pl tracking-wide"
              placeholder="——————————@———"
            />
          </label>

          <label className="block mb-6">
            <span className="block font-mono-pl text-[10px] uppercase tracking-[0.28em] text-[#B6B6BE] mb-2">
              Chiave d&apos;accesso
            </span>
            <div className="relative">
              <input
                type={show ? "text" : "password"}
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="staff-login-password"
                className="staff-input w-full px-4 py-3 pr-12 rounded-lg text-sm text-[#F5F5F5] font-mono-pl tracking-wide"
                placeholder="••••••••••••"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B6B6BE] hover:text-[#F5F5F5]"
                aria-label={show ? "Nascondi" : "Mostra"}
              >
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </label>

          {err && (
            <div className="staff-error mb-5 flex items-start gap-2 px-4 py-3 rounded-lg">
              <ShieldAlert size={14} className="text-[#E26B6B] mt-0.5 shrink-0" />
              <span className="text-xs text-[#E26B6B]" data-testid="staff-login-error">
                {err}
              </span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="staff-login-submit"
            className="staff-submit w-full rounded-lg px-5 py-3.5 text-xs uppercase tracking-[0.28em] font-bold disabled:opacity-60"
          >
            {loading ? "Verifica in corso…" : "Autenticati"}
          </button>

          <div className="staff-watermark mt-8 flex items-start gap-2">
            <AlertTriangle size={12} className="text-[#E26B6B]/70 mt-0.5 shrink-0" />
            <p className="font-mono-pl text-[9px] uppercase tracking-[0.32em] text-[#B6B6BE]/60 leading-relaxed">
              Documento interno · Non divulgare · Tracciato e auditato
            </p>
          </div>
        </form>

        <p className="mt-8 text-center font-mono-pl text-[9px] uppercase tracking-[0.32em] text-[#B6B6BE]/40">
          PURELIFE · STAFF.OPS · CHANNEL 7
        </p>
      </div>
    </div>
  );
}
